import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, Users, CheckCircle, Star, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';

declare global {
  interface Window {
    Calendly: any;
  }
}

const BookDemo = () => {
  const navigate = useNavigate();
  const [emailCaptured, setEmailCaptured] = useState(false);

  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "CEO at TechFlow",
      content: "The demo was eye-opening. Launched our first campaign during the call!",
      rating: 5
    },
    {
      name: "Mike Rodriguez", 
      role: "VP Sales at DataSync",
      content: "30-minute demo saved us $180K/year and 40 hours/week",
      rating: 5
    }
  ];

  const demoAgenda = [
    "See your first AI-written email in 2 minutes",
    "Watch us find your ideal prospects from 250M+ contacts",
    "Launch a live campaign together (if you want)",
    "Get honest pricing and timeline",
    "Q&A - ask us anything"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/new')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div className="flex items-center gap-4">
            <Badge variant="secondary" className="bg-green-100 text-green-800">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse" />
              Live booking available
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Trust Building */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge className="mb-4 bg-blue-100 text-blue-800">
                15-Minute Demo
              </Badge>
              
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                See Munger book your first meeting in{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  real-time
                </span>
              </h1>

              <p className="text-xl text-gray-600 mb-8">
                Watch us write emails, find prospects, and launch a campaign live on our call. 
                No slides, just results.
              </p>

              {/* Social Proof */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-yellow-600" />
                  <span className="font-semibold text-yellow-900">
                    2,847 demos booked this month
                  </span>
                </div>
                <p className="text-sm text-yellow-800">
                  Average demo-to-customer: 47% (industry avg: 20%)
                </p>
              </div>

              {/* What to Expect */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  What we'll cover:
                </h3>
                <ul className="space-y-3">
                  {demoAgenda.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Testimonials */}
              <div className="space-y-4">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-center gap-1 mb-2">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-2">"{testimonial.content}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full" />
                      <div>
                        <p className="font-semibold text-sm text-gray-900">{testimonial.name}</p>
                        <p className="text-xs text-gray-600">{testimonial.role}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Trust Badges */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex items-center gap-6 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>15 min max</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>No slides</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span>1-on-1 with founder</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Calendly Embed */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg p-6 sticky top-24"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                Pick a time that works for you
              </h2>
              
              {/* Calendly embed */}
              <div 
                className="calendly-inline-widget" 
                data-url="https://calendly.com/airlius-hts/munger-ai-demo"
                style={{ minWidth: '320px', height: '700px' }}
              />
              
              {/* Fallback */}
              <div className="text-center mt-4">
                <p className="text-sm text-gray-600 mb-2">
                  Prefer to schedule manually?
                </p>
                <a 
                  href="mailto:demo@munger.ai" 
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Email us at demo@munger.ai
                </a>
              </div>

              {/* Urgency */}
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-medium text-center">
                  🔥 Only 37 beta spots remaining (closing in 9 days)
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookDemo; 